import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageProvider';
import { useThemeContext } from '../context/ThemeProvider';
import ActionButton from './common/ActionButton';

interface HelpSectionData {
  key: string;
  icon: string;
}

export default function HelpSection(): JSX.Element {
  const [showHelp, setShowHelp] = useState<boolean>(false);
  const { t } = useLanguage();
  const { getClass } = useThemeContext();

  const getThemeClass = (type: string): string => {
    try {
      return getClass(type) || '';
    } catch {
      const fallback: Record<string, string> = {
        background: 'bg-white',
        text: 'text-gray-900',
        button: 'bg-blue-600 hover:bg-blue-700 text-white',
        grid: 'bg-gray-100',
        border: 'border-gray-300',
        input: 'border-gray-300 bg-white text-gray-900',
        card: 'bg-white border-gray-200 shadow-sm',
        textSecondary: 'text-gray-600'
      };
      return fallback[type] || '';
    }
  };

  // 渲染Markdown風格的文字
  const renderMarkdownText = (text: string | undefined): JSX.Element[] | null => {
    if (!text) return null;
    
    return text.split('\n').map((line, index) => {
      // 處理粗體文字 **text**
      const boldRegex = /\*\*(.*?)\*\*/g;
      const parts = line.split(boldRegex);
      
      return (
        <div key={index} className="mb-1">
          {parts.map((part, partIndex) => {
            if (partIndex % 2 === 1) {
              // 奇數索引是粗體文字
              return <strong key={partIndex}>{part}</strong>;
            }
            
            // 處理URL鏈接
            const urlRegex = /(https?:\/\/[^\s]+)/g;
            if (urlRegex.test(part)) {
              return part.split(urlRegex).map((segment, segmentIndex) => {
                if (segment.match(urlRegex)) {
                  return (
                    <a 
                      key={`${partIndex}-${segmentIndex}`}
                      href={segment}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:text-blue-700 underline break-all"
                    >
                      {segment}
                    </a>
                  );
                }
                return segment;
              });
            }
            
            return part;
          })}
        </div>
      );
    });
  };

  const helpSections: HelpSectionData[] = [
    { key: 'playback', icon: '🎵' },
    { key: 'drumGrid', icon: '🥁' },
    { key: 'drumComponents', icon: '🥁' },
    { key: 'settings', icon: '⚙️' },
    { key: 'groove', icon: '🎶' },
    { key: 'patterns', icon: '📋' },
    { key: 'drumset', icon: '🛠️' },
    { key: 'soundManagement', icon: '🎵' },
    { key: 'aiGenerate', icon: '🤖' },
    { key: 'fileOps', icon: '💾' },
    { key: 'ascii', icon: '📝' },
    { key: 'infoSharing', icon: '🌐' }
  ];

  return (
    <div className={`p-3 rounded ${getThemeClass('grid')} ${getThemeClass('border')}`}>
      {!showHelp && (
        <div className="flex items-center space-x-3">
          <h2 className="text-xl font-bold">❓ {t('helpTitle')}</h2>
          <ActionButton
            onClick={() => setShowHelp(true)}
            icon="📖"
            text={t('showHelp')}
            tooltip={t('showHelp')}
            variant="info"
          />
        </div>
      )}
      
      {showHelp && (
        <>
          <div className="flex items-center space-x-3 mb-4">
            <h2 className="text-xl font-bold">❓ {t('helpTitle')}</h2>
            <ActionButton
              onClick={() => setShowHelp(false)}
              icon="📚"
              text={t('hideHelp')}
              tooltip={t('hideHelp')}
              variant="secondary"
            />
          </div>
          
          <div className="grid gap-4 md:grid-cols-2">
            {helpSections.map(section => (
              <div key={section.key} className={`p-3 rounded ${getThemeClass('card')} border ${getThemeClass('border')}`}>
                <h3 className="font-semibold mb-2">
                  {t(`helpContent.${section.key}`)?.split('\n')[0]?.replace(/\*\*/g, '').trim() || `${section.icon} ${section.key}`}
                </h3>
                <div className={`text-sm ${getThemeClass('textSecondary')} leading-relaxed`}>
                  {renderMarkdownText(t(`helpContent.${section.key}`)?.split('\n').slice(1).join('\n'))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
} 